import { useParams, Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-100 mb-2">电影未找到</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-400">返回首页</Link>
        </div>
      </div>
    );
  }

  const categoryName = getCategoryName(movie.category);
  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 4);

  return (
    <>
      <SEO
        title={`${movie.title} - 97影视${categoryName}高清在线观看`}
        description={`${movie.title}，${movie.description.substring(0, 120)}...97影视免费在线观看${categoryName}。`}
        keywords={`${movie.title},${categoryName},97影视,高清电影,在线观看,${movie.tags.join(',')}`}
        canonical={`https://www.97yingshi.com/article/${movie.id}`}
        ogTitle={`${movie.title} - 97影视`}
        ogDescription={movie.description.substring(0, 160)}
        ogImage={movie.poster}
        ogUrl={`https://www.97yingshi.com/article/${movie.id}`}
        ogType="video.movie"
        twitterCard="summary_large_image"
        twitterTitle={`${movie.title} - 97影视`}
        twitterDescription={movie.description.substring(0, 160)}
        twitterImage={movie.poster}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Movie',
          name: movie.title,
          description: movie.description,
          image: movie.poster,
          dateCreated: movie.releaseDate,
          director: { '@type': 'Person', name: movie.director },
          actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
          genre: movie.tags,
          countryOfOrigin: { '@type': 'Country', name: movie.region },
        }}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: categoryName, href: `/category/${movie.category}` },
          { label: movie.title },
        ]}
      />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <article>
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-full md:w-[300px] flex-shrink-0">
              <div className="aspect-[2/3] rounded-lg overflow-hidden">
                <img
                  src={movie.poster}
                  alt={`${movie.title} - 97影视`}
                  title={`${movie.title} - 97影视${categoryName}高清在线观看`}
                  className="w-full h-full object-cover"
                  width="300"
                  height="450"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                {movie.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{movie.originalTitle}</p>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-500 text-sm font-semibold">
                  <i className="ri-star-fill"></i>
                  {movie.rating}
                </span>
                <span className="text-sm text-foreground-300">{movie.year}</span>
                <span className="text-sm text-foreground-300">{movie.duration}</span>
                <span className="text-sm text-foreground-300">{movie.region}</span>
                {movie.isNew && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-foreground-50">
                    NEW
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap mb-4">
                {movie.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="space-y-2 text-sm text-foreground-300 mb-5">
                <p><strong className="text-foreground-200">导演：</strong>{movie.director}</p>
                <p><strong className="text-foreground-200">主演：</strong>{movie.actors.join('、')}</p>
                <p><strong className="text-foreground-200">上映日期：</strong>{movie.releaseDate}</p>
                <p><strong className="text-foreground-200">播放量：</strong>{movie.views.toLocaleString()}次</p>
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-5">
                <h3 className="text-sm font-semibold text-foreground-100 mb-2">剧情简介</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
              </div>

              <div className="flex items-center gap-3">
                <button className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap">
                  <i className="ri-play-fill text-base"></i>
                  立即播放
                </button>
                <button className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-background-100 text-foreground-200 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap border border-background-300/50">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="mt-10 md:mt-14">
            <div className="mb-5">
              <h2 className="text-lg font-heading font-bold text-foreground-50">
                相关推荐
              </h2>
              <p className="text-xs text-foreground-500 mt-1">更多{movie.tags.join('、')}类电影</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}